import axios, { type AxiosRequestConfig } from 'axios';
import { useContext, useMemo } from 'react';
import { config } from '../../../modules/config';
import { AuthContext } from '../../../modules/context/authContext';
import type { SuccessEnvelop } from '../../../modules/context/guestAuthContext';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import type {
  CreateLockPayload,
  LockRecord,
  ProviderLock,
  UpdateLockPayload,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';

/**
 * Admin CRUD for managed locks against `v3/locks`, plus the provider-device
 * listing (`v3/locks/provider`).
 *
 * Thin axios-backed service hook (same shape as useGuestPortalThemes): requests
 * flow through the global axios interceptors, carry the Cognito token and the
 * selected property's `property-id` header, and unwrap `data.result`.
 */
export interface LocksService {
  list: () => Promise<LockRecord[]>;
  get: (uuid: string) => Promise<LockRecord>;
  create: (payload: CreateLockPayload) => Promise<LockRecord>;
  update: (uuid: string, payload: UpdateLockPayload) => Promise<LockRecord>;
  remove: (uuid: string) => Promise<void>;
  listProviderLocks: () => Promise<ProviderLock[]>;
}

export const useLocks = (): LocksService => {
  const { credentialsInfo } = useContext(AuthContext) || {};
  const { selectedProperty } = useContext(UserPropertiesContext)!;

  const token = credentialsInfo?.token;
  const propertyHeaderId = selectedProperty?.property.uuid;

  return useMemo<LocksService>(() => {
    const request = async <R>(reqConfig: AxiosRequestConfig): Promise<R> => {
      const headers: Record<string, string> = {};
      if (token) headers.authorization = token;
      if (propertyHeaderId) headers['property-id'] = propertyHeaderId;

      const { data } = await axios.request<SuccessEnvelop<R>>({
        baseURL: config.bookingEngine.endpoint,
        ...reqConfig,
        headers: { ...headers, ...reqConfig.headers },
      });
      return data.result as R;
    };

    return {
      list: () => request<LockRecord[]>({ url: endpoints.LOCKS_V3, method: 'get' }),

      get: (uuid) =>
        request<LockRecord>({ url: `${endpoints.LOCKS_V3}/${uuid}`, method: 'get' }),

      create: (payload) =>
        request<LockRecord>({ url: endpoints.LOCKS_V3, method: 'post', data: payload }),

      update: (uuid, payload) =>
        request<LockRecord>({
          url: `${endpoints.LOCKS_V3}/${uuid}`,
          method: 'patch',
          data: payload,
        }),

      remove: (uuid) =>
        request<void>({ url: `${endpoints.LOCKS_V3}/${uuid}`, method: 'delete' }),

      listProviderLocks: () =>
        request<ProviderLock[]>({ url: endpoints.LOCKS_PROVIDER, method: 'get' }),
    };
  }, [token, propertyHeaderId]);
};
